import { Box, Chip, useTheme } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CircleIcon from '@mui/icons-material/Circle';
import { Business_Profile_Enum, User_Role_Enum } from '@kheops/graphql';
import { currentContextAtom } from '../../state';
import { orderListPageAtom, OrderListUiState, orderListUiStateAtom } from '../state/state';
import { OrdersCountByCategoryMap } from './OrderList';
import OrderCompanyFilter from './OrderCompanyFilter';

type OrderFiltersProps = {
  ordersCountByCategory: OrdersCountByCategoryMap;
}

export default function OrderFilters({ ordersCountByCategory }: OrderFiltersProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'order']);
  const { realm, roleName, userAdministrativeAreas, userBrands } = useAtomValue(currentContextAtom);
  const [orderListUiState, setOrderListUiState] = useAtom(orderListUiStateAtom);
  const setOrderListPage = useSetAtom(orderListPageAtom);

  const onOrderListUiStateUpdate = (newState: OrderListUiState): void => {
    setOrderListPage(1);

    window.history.replaceState({
      ...window.history.state,
      uiState: {
        ...newState,
        page: 1,
      },
    }, '');
  };

  const updateOrderUiState = useCallback((state: OrderListUiState): void => {
    setOrderListUiState(state);
    onOrderListUiStateUpdate(state);
  }, []);

  const handleOnlyMyOrdersChange = useCallback((): void => {
    const newUiState = {
      ...orderListUiState,
      onlyMyOrders: !orderListUiState.onlyMyOrders,
    };

    updateOrderUiState(newUiState);
  }, [orderListUiState]);

  const handleOnlyOrdersWhereActionIsNeededChange = useCallback((): void => {
    const newUiState: OrderListUiState = {
      ...orderListUiState,
      onlyOrdersWhereActionIsNeeded: !orderListUiState.onlyOrdersWhereActionIsNeeded,
    };

    updateOrderUiState(newUiState);
  }, [orderListUiState]);

  const handleMyClientsOrdersChange = useCallback((): void => {
    const newUiState = {
      ...orderListUiState,
      onlyMyClients: !orderListUiState.onlyMyClients,
    };

    updateOrderUiState(newUiState);
  }, [orderListUiState]);

  const handleGetOrdersWithDispute = useCallback((): void => {
    const newUiState = {
      ...orderListUiState,
      ordersWithDispute: !orderListUiState.ordersWithDispute,
    };

    updateOrderUiState(newUiState);
  }, [orderListUiState]);

  const shouldDisplayMyClientsFilter = realm === Business_Profile_Enum.Supplier
    && [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(roleName)
    && (
      (!!userBrands?.length && !userBrands[0]?.has_all_brands)
      || (!!userAdministrativeAreas?.length && !userAdministrativeAreas[0].has_all_areas)
    );

  return (
    <Box sx={{ display: 'flex', gap: 1, width: '100%', overflowX: 'auto' }}>
      {realm === Business_Profile_Enum.Buyer && roleName !== User_Role_Enum.BuyerFinance && (
        <Chip
          label={`${t('order:my_orders')} (${ordersCountByCategory.CREATED_BY_ME})`}
          disabled={!ordersCountByCategory.CREATED_BY_ME}
          variant="filter"
          className={orderListUiState.onlyMyOrders && !!ordersCountByCategory.CREATED_BY_ME ? 'Kheops-selected' : ''}
          onClick={() => handleOnlyMyOrdersChange()}
        />
      )}
      {shouldDisplayMyClientsFilter && (
        <Chip
          label={`${t('order:my_clients')} (${ordersCountByCategory.MY_CLIENTS})`}
          disabled={!ordersCountByCategory.MY_CLIENTS}
          variant="filter"
          className={orderListUiState.onlyMyClients && !!ordersCountByCategory.MY_CLIENTS ? 'Kheops-selected' : ''}
          onClick={() => handleMyClientsOrdersChange()}
        />
      )}
      <Chip
        label={(
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {t('order:orders_where_action_needed')} ({ordersCountByCategory.WITH_ACTION_NEEDED})
            {!!ordersCountByCategory.WITH_ACTION_NEEDED && (
              <CircleIcon sx={{ ml: 1, fontSize: '0.4375rem', fill: theme.palette.error.main as unknown as string }} />
            )}
          </Box>
        )}
        disabled={!ordersCountByCategory.WITH_ACTION_NEEDED}
        variant="filter"
        className={orderListUiState.onlyOrdersWhereActionIsNeeded && !!ordersCountByCategory.WITH_ACTION_NEEDED ? 'Kheops-selected' : ''}
        onClick={() => handleOnlyOrdersWhereActionIsNeededChange()}
      />
      <Chip
          label={`${t('order:disputes')} (${ordersCountByCategory.DISPUTE})`}
          disabled={!ordersCountByCategory.DISPUTE}
          variant="filter"
          className={orderListUiState.ordersWithDispute && !!ordersCountByCategory.DISPUTE ? 'Kheops-selected' : ''}
          onClick={() => handleGetOrdersWithDispute()}
        />
      <OrderCompanyFilter />
    </Box>
  );
}
